import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import lowerFirst from 'lodash/lowerFirst';

import DetailsModal from '../../global/DetailsModal';

export default function WithModifierDetails(WrappedComponent) {
  class withModifierDetails extends WrappedComponent {
    getActiveOption() {
      const {
        productDetails: {
          sku,
        },
        modifier: {
          activeStateProp,
          options = [],
          onSelectFunction,
        },
        selectedModifierOptions,
      } = this.props;

      return options.find(({ value, selected }) => (
        (activeStateProp && value === this.props[lowerFirst(activeStateProp)])
          || selectedModifierOptions?.find((selectedModifierOption) => selectedModifierOption.value === value)
          || selected
          || (onSelectFunction?.toLowerCase() === 'setmaterial' && sku.includes(value))
      ));
    }

    shouldRender() {
      const {
        selectedModifierOptions,
        modifier: {
          requiredActiveModifierOptions,
        },
      } = this.props;

      if (!requiredActiveModifierOptions?.length) {
        return true;
      }

      return requiredActiveModifierOptions?.every((requiredModifier) => (
        selectedModifierOptions.some((modifierOption) => (
          modifierOption.value === requiredModifier.value
        ))
      ));
    }

    selectModifierOption(option) {
      const {
        modifier,
        toggleModifierOptionAction,
        withRedirect = true,
      } = this.props;

      toggleModifierOptionAction(
        {
          modifier,
          option,
        },
        withRedirect,
      );
    }

    shouldSelectDefaultActiveModifier() {
      const {
        modifier: { onSelectFunction },
      } = this.props;

      // we only select a default active option if this modifier should render
      // and does not have an active option
      // and is not in a disabled state (set by data fetch request)
      return (
        this.shouldRender()
        && !this.getActiveOption()
        && !this.props.disabled
        && onSelectFunction !== 'SetMaterialColor'
      );
    }

    selectDefaultActiveModifierOption() {
      const {
        modifier: {
          options: [option] = [],
        },
      } = this.props;

      // we only select the first element as default active option if it has at least 1 option
      if (option) {
        this.selectModifierOption(option);
      }
    }

    componentDidMount() {
      if (this.shouldSelectDefaultActiveModifier()) {
        this.selectDefaultActiveModifierOption();
      }
    }

    componentDidUpdate(prevProps) {
      // we don't select default active option in prop updates if we should not render the modifier
      // or we it has an active option alreadu
      // or it is in a disabled state (set by data fetch request) or same as prev value
      // or due to sku is changing
      const shouldNotSelectDefaultActiveOption = !this.shouldSelectDefaultActiveModifier()
        || this.props.disabled === prevProps.disabled
        || this.props.productDetails.sku !== prevProps.productDetails.sku;

      if (shouldNotSelectDefaultActiveOption) {
        return;
      }

      this.selectDefaultActiveModifierOption();
    }

    render() {
      const {
        fetchProductDetailsAction,
        modifier: {
          className,
          title,
          showModifierValue,
          subTitle,
          modals: [modal],
          slug,
          options,
          type,
        },
      } = this.props;

      const activeOption = this.getActiveOption();

      const shouldRender = this.shouldRender();
      const applyWrapOptionsStyle = type === 'ColorPicker' && options.length >= 5;

      return shouldRender && (
        <div className={classNames('pdp-options', slug?.toLowerCase(), className)}>
          <div className="pdp-options__header">
            <span className="pdp-options__header-title">{title}</span>
            {showModifierValue && (
              <span className="pdp-options__header-value">{activeOption?.description}</span>
            )}
          </div>
          {subTitle && <div className="pdp-options__description">{subTitle}</div>}
          <div
            className={classNames('pdp-options-wrapper', className)}
            style={{
              flexWrap: applyWrapOptionsStyle ? 'wrap' : 'unset',
            }}
          >
            {super.render(activeOption)}
            {modal && (
              <span
                className="pdp-details-info"
                style={{
                  justifyContent: applyWrapOptionsStyle ? 'flex-start' : 'flex-end',
                }}
              >
                <DetailsModal
                  teaserCopy={modal.teaserCopy}
                  teaserIcon={modal.teaserIcon}
                  salesTiers={modal.salesTiers}
                  popUpCopy={modal.copy}
                  fetchProductDetails={fetchProductDetailsAction}
                  header={modal.header}
                  footnote={modal.footnote}
                  asset={modal.assets && modal.assets[0]}
                  links={modal.links}
                />
              </span>
            )}
          </div>
        </div>
      );
    }
  }

  withModifierDetails.propTypes = {
    modifier: PropTypes.shape({
      className: PropTypes.string,
      title: PropTypes.string,
      showModifierValue: PropTypes.bool,
      subTitle: PropTypes.string,
      slug: PropTypes.string,
      requiredActiveModifierOptions: PropTypes.array,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          description: PropTypes.string,
          active: PropTypes.bool,
          modals: PropTypes.array,
        }),
      ),
    }),
    selectedModifierOptions: PropTypes.array,
    fetchProductDetailsAction: PropTypes.func,
    toggleModifierOptionAction: PropTypes.func,
    withRedirect: PropTypes.bool,
    disabled: PropTypes.bool,
  };

  return withModifierDetails;
}
